/**
 * Simple event service to broadcast messages across components
 */

type EventCallback = (data: any) => void;

// Event types
export enum EventType {
  NEW_ALERT = 'new_alert',
  SENSOR_UPDATE = 'sensor_update',
}

class EventService {
  private listeners: Map<EventType, EventCallback[]> = new Map();

  /**
   * Subscribe to an event
   * @param eventType The event type to subscribe to
   * @param callback The callback to execute when the event is emitted
   * @returns A function to unsubscribe
   */
  subscribe(eventType: EventType, callback: EventCallback): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }

    const callbacks = this.listeners.get(eventType)!;
    callbacks.push(callback);

    // Return unsubscribe function
    return () => {
      const index = callbacks.indexOf(callback);
      if (index !== -1) {
        callbacks.splice(index, 1);
      }
    };
  }

  /**
   * Emit an event
   * @param eventType The event type to emit
   * @param data The data to pass to subscribers
   */
  emit(eventType: EventType, data: any): void {
    if (!this.listeners.has(eventType)) {
      return;
    }

    const callbacks = this.listeners.get(eventType)!;
    callbacks.forEach(callback => callback(data));
  }
}

// Singleton instance
export const eventService = new EventService();